<template>
  <div class="card mr-3">
    <div class="card-header card-header-flex align-items-center">
      <div class="d-flex flex-column justify-content-center mr-auto">
        <h5 class="mb-0">
          <strong>{{ $t("utilisateur.listeUtilisateur") }}</strong>
        </h5>
        <div class="mr-4 mr-sm-4">
          <div class="d-flex align-items-center">
            <a-spin v-if="spinLoader" class="mr-1" />
            <span v-else :style="textColor" class="font-weight-bold">{{
              userNumber
            }}</span>
            <span class="mx-1">/</span>
            <span class="text-muted">{{ userPackLimit }}</span>
            <span class="ml-1 d-none d-sm-inline">{{ $t("menu.connection") }}</span>
            <span class="ml-1 d-sm-none">{{ $t("menu.connection") }}</span>
          </div>
        </div>
      </div>
      <div class="d-flex align-items-center">
        <a-switch
          :checked="checkedActiveUser"
          :checked-children="$t('utilisateur.active')"
          :un-checked-children="$t('utilisateur.nonActive')"
          @change="onChange"
        />
        <a-switch
          v-if="canManageUserSessions"
          class="ml-2"
          :checked="filterActiveSessionsOnly"
          :checked-children="$t('utilisateur.withActiveSessions')"
          :un-checked-children="$t('utilisateur.allUsersFilter')"
          @change="onActiveSessionsFilterChange"
        />
      </div>
    </div>
    <div class="card-body">
      <a-alert
        v-if="userPackLimit && userNumber >= userPackLimit"
        class="mb-3"
        type="error"
        :message="$t('error.maxUsersReached')"
        banner
        show-icon
      />

      <a-button type="user-add" class="mb-3" @click="showModal">
        <a-icon type="user-add" @click="showModal" />{{ $t("action.ajouter") }}
      </a-button>
      <a-button
        type="default"
        class="mb-3 ml-2"
        @click="generatePdfLogins"
        :loading="loadingLogins"
      >
        <a-icon type="file-pdf" />
        {{ $t("all.printLoginCredentials") }}
      </a-button>
      <a-table
        :data-source="data"
        :columns="columns"
        rowKey="_id"
        :scroll="{ x: 'max-content' }"
        :loading="tableLoading"
      >
        <a-button
          slot="operation0"
          type="primary"
          slot-scope="text, record"
          @click="() => updatePasswordModal(record)"
        >
          <a-icon type="lock" /> {{ $t("action.changer") }}
        </a-button>
        <a-button
          slot="operation"
          slot-scope="text, record"
          @click="() => updateModal(record)"
        >
          <a-icon type="select" /> {{ $t("action.modifier") }}
        </a-button>
        <template slot="buildings" slot-scope="text, record">
          <div
            v-for="(building, index) in record.buildings"
            :key="building._id"
            class="d-inline"
          >
            <a-tag color="#2db7f5"> {{ building.name }}</a-tag>
            <div v-if="index % 2 == 1"><br /></div>
          </div>
        </template>
        <template slot="status" slot-scope="text">
          <div v-if="text == 'active'">
            <a-tag color="#87d068">{{ $t("utilisateur.active") }}</a-tag>
          </div>
          <div v-else>
            <a-tag color="#f50">{{ $t("utilisateur.nonActive") }}</a-tag>
          </div>
        </template>
        <template slot="sessionStatus" slot-scope="text, record">
          <div>
            <a-badge
              :status="record.hasActiveSessions ? 'success' : 'default'"
              :text="
                record.hasActiveSessions
                  ? $t('utilisateur.sessionOnline')
                  : $t('utilisateur.sessionOffline')
              "
            />
            <a-button
              class="ml-2"
              size="small"
              icon="eye"
              :loading="sessionLoadingUserId === record._id"
              @click="showUserSessions(record)"
            >
              {{
                $t("utilisateur.viewSessions", {
                  count: record.activeSessionCount || 0,
                })
              }}
            </a-button>
          </div>
        </template>
        <template slot="operation2" slot-scope="text, record">
          <div v-if="user.userName != record.userName">
            <a-popconfirm
              v-if="record.status == 'active'"
              :title="$t('utilisateur.desactiverAlert')"
              @confirm="() => onDelete(record)"
            >
              <a-button type="danger">
                <a-icon type="delete" href="javascript:;" />{{
                  $t("action.desactiver")
                }}
              </a-button>
            </a-popconfirm>
            <a-popconfirm
              v-else
              :title="$t('utilisateur.activerAlert')"
              @confirm="() => onActivate(record)"
            >
              <a-button type="primary">
                <a-icon type="user" href="javascript:;" />{{
                  $t("action.activer")
                }}
              </a-button>
            </a-popconfirm>
          </div>
        </template>
        <div
          slot="filterDropdown"
          slot-scope="{
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
            column,
          }"
          style="padding: 8px"
        >
          <a-input
            v-ant-ref="(c) => (searchInput = c)"
            :placeholder="`${$t('personnel.chercher')} ${column.title}`"
            :value="selectedKeys[0]"
            style="width: 188px; margin-bottom: 8px; display: block"
            @change="
              (e) => setSelectedKeys(e.target.value ? [e.target.value] : [])
            "
            @pressEnter="
              () => handleSearch(selectedKeys, confirm, column.dataIndex)
            "
          />
          <a-button
            type="primary"
            icon="search"
            size="small"
            style="width: 90px; margin-right: 8px"
            @click="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
            >{{ $t("paiement.chercher") }}</a-button
          >
          <a-button
            size="small"
            style="width: 90px"
            @click="() => handleReset(clearFilters)"
            >{{ $t("action.reinitialiser") }}</a-button
          >
        </div>
        <a-icon
          slot="filterIcon"
          slot-scope="filtered"
          type="search"
          :style="{ color: filtered ? '#108ee9' : undefined }"
        />
        <template slot="customRender" slot-scope="text, record, index, column">
          <span v-if="searchText && searchedColumn === column.dataIndex">
            <template
              v-for="(fragment, i) in text
                .toString()
                .split(new RegExp(`(?<=${searchText})|(?=${searchText})`, 'i'))"
            >
              <mark
                v-if="fragment.toLowerCase() === searchText.toLowerCase()"
                :key="i"
                class="highlight"
                >{{ fragment }}</mark
              >
              <template v-else>{{ fragment }}</template>
            </template>
          </span>
          <template v-else>{{ text }}</template>
        </template>
      </a-table>
        <a-modal
          v-model="sessionModalVisible"
          :title="
            $t('utilisateur.activeSessionsFor', {
              user: selectedSessionUser ? selectedSessionUser.userName : '',
            })
          "
          :footer="null"
          :width="900"
          @cancel="closeSessionModal"
        >
          <a-table
            rowKey="familyId"
            size="small"
            :columns="sessionColumns"
            :data-source="selectedUserSessions"
            :loading="sessionsLoading"
            :pagination="false"
          >
            <template slot="sessionDevice" slot-scope="text, record">
              {{ (record.device && record.device.label) || $t("utilisateur.unknownDevice") }}
            </template>
            <template slot="sessionLastActive" slot-scope="text">
              {{ formatSessionDate(text) }}
            </template>
            <template slot="sessionExpiresAt" slot-scope="text">
              {{ formatSessionDate(text) }}
            </template>
            <template slot="sessionActions" slot-scope="text, record">
              <a-popconfirm
                v-if="
                  selectedSessionUser &&
                  selectedSessionUser._id !== user._id
                "
                :title="$t('utilisateur.revokeSessionConfirm')"
                @confirm="revokeSelectedUserSession(record)"
              >
                <a-button
                  type="link"
                  size="small"
                  class="text-danger"
                  :loading="revokingFamilyId === record.familyId"
                >
                  {{ $t("utilisateur.revokeSession") }}
                </a-button>
              </a-popconfirm>
            </template>
          </a-table>
          <div class="mt-3 text-right">
            <a-popconfirm
              v-if="
                selectedSessionUser &&
                selectedSessionUser._id !== user._id &&
                selectedUserSessions.length
              "
              :title="$t('utilisateur.revokeAllSessionsConfirm')"
              @confirm="revokeSelectedUserSessions"
            >
              <a-button type="danger" :loading="revokingSessions">
                <a-icon type="logout" />
                {{ $t("utilisateur.revokeAllSessions") }}
              </a-button>
            </a-popconfirm>
          </div>
        </a-modal>
        <a-modal
          v-model="visibleModal"
          @cancel="handleCancel"
          :title="$t('all.enterInformationBelow')"
          :width="750"
          :height="1200"
          :footer="false"
        >
          <a-form :form="form" class="container" @submit="addUtilisateur">
            <div class="row">
              <div class="col-lg-6">
                <a-form-item :label="$t('utilisateur.role')">
                  <a-select
                    show-search
                    :autoFocus="true"
                    option-filter-prop="children"
                    :filter-option="filterOption"
                    :placeholder="$t('utilisateur.role')"
                    v-decorator="[
                      'role',
                      {
                        rules: [
                          {
                            required: true,
                            message: $t('requis.role'),
                          },
                        ],
                      },
                    ]"
                  >
                    <a-select-option
                      :value="item._id"
                      v-for="item in roleList"
                      :key="item._id"
                    >
                      {{ item.role }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
              <div class="col-lg-6">
                <a-form-item :label="$t('profil.nomUser')">
                  <a-input
                    :placeholder="$t('profil.nomUser')"
                    v-decorator="[
                      'userName',
                      {
                        rules: [
                          {
                            required: true,
                            message: $t('requis.username'),
                          },
                          {
                            validator: validateNoWhitespace,
                            message: $t('requis.noWhitespace'),
                          },
                        ],
                      },
                    ]"
                  />
                </a-form-item>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6">
                <a-form-item :label="$t('profil.mdp')">
                  <a-input
                    :placeholder="$t('profil.mdp')"
                    type="password"
                    v-decorator="[
                      'password',
                      {
                        rules: [
                          {
                            required: true,
                            message: $t('requis.mdp'),
                          },
                        ],
                      },
                    ]"
                  />
                </a-form-item>
              </div>
              <div class="col-lg-6">
                <a-form-item :label="$t('utilisateur.confirmerMdp')">
                  <a-input
                    :placeholder="$t('utilisateur.confirmerMdp')"
                    type="password"
                    v-decorator="[
                      'confirmPassword',
                      {
                        rules: [
                          {
                            required: true,
                            message: $t('requis.confirmation'),
                          },
                        ],
                      },
                    ]"
                  />
                </a-form-item>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-12">
                <a-form-item :label="$t('utilisateur.acces')">
                  <a-select
                    show-search
                    :autoFocus="true"
                    :allowClear="true"
                    mode="multiple"
                    option-filter-prop="children"
                    :filter-option="filterOption"
                    style="width: 100%"
                    :placeholder="$t('utilisateur.acces')"
                    v-decorator="[
                      'buildings',
                      {
                        rules: [
                          {
                            required: true,
                            message: $t('requis.role'),
                          },
                        ],
                      },
                    ]"
                  >
                    <a-select-option
                      :value="item._id"
                      v-for="item in buildingList"
                      :key="item._id"
                    >
                      {{ item.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
            </div>
            <div class="form-actions mt-0">
              <a-button
                style="width: 150px"
                type="primary"
                htmlType="submit"
                :loading="loadingUtilisateur"
                :disabled="loadingUtilisateur"
                class="mr-3"
              >
                {{ $t("action.ajouter") }}
              </a-button>
              <a-button
                key="back"
                @click="handleCancel"
                :disabled="loadingUtilisateur"
              >
                {{ $t("action.annuler") }}
              </a-button>
            </div>
          </a-form>
        </a-modal>
        <a-modal
          v-model="realVisibleModalPassword"
          @cancel="
            () => {
              realVisibleModalPassword = false;
            }
          "
          :title="$t('utilisateur.changerMdp')"
          :width="750"
          :height="1200"
          :footer="false"
        >
          <a-form
            :form="formPassword"
            class="container"
            @submit="updatePassword"
            >
                <div class="row">
                  <div class="col-lg-6">
                    <a-form-item :label="$t('profil.mdp')">
                      <a-input-password
                        :placeholder="$t('profil.mdp')"
                        type="password"
                        v-decorator="[
                          'password',
                          {
                            rules: [
                              {
                                validator: validateStrongPassword,
                              },
                            ],
                          },
                        ]"
                      />
                    </a-form-item>
                  </div>
                  <div class="col-lg-6">
                    <a-form-item :label="$t('utilisateur.confirmerMdp')">
                      <a-input-password
                        :placeholder="$t('utilisateur.confirmerMdp')"
                        type="password"
                        v-decorator="[
                          'confirmPassword',
                          {
                            rules: [
                              {
                                required: true,
                                message: $t('requis.confirmation'),
                              },
                              {
                                validator: validatePassCh2,
                                message: $t('requis.confirmation'),
                              },
                            ],
                          },
                        ]"
                      />
                    </a-form-item>
                  </div>
                </div>
                <div v-if="fetchToken" class="text-center py-3">
                  <a-spin />
                </div>
                <vue-qr
                  v-else-if="qrData"
                  :text="qrData"
                  :logoSrc="getBuilding.logo"
                  :callback="getQrImage"
                  qid="admin-user-qr"
                ></vue-qr>
                <div class="form-actions mt-0">
                  <a-button
                    style="width: 150px"
                    type="primary"
                    htmlType="submit"
                    :loading="loadingUtilisateur"
                    class="mr-3"
                  >
                    {{ $t("action.modifier") }}
                  </a-button>
                  <a-button
                    key="back"
                    @click="
                      () => {
                        realVisibleModalPassword = false;
                      }
                    "
                    :disabled="loadingUtilisateur"
                  >
                    {{ $t("action.annuler") }}
                  </a-button>
                </div>
              </a-form>
          </a-modal>
        <a-modal
          v-model="visibleModalPassword"
          @cancel="
            () => {
              visibleModalPassword = false;
            }
          "
          :title="$t('all.enterInformationBelow')"
          :width="750"
          :height="1200"
          :footer="false"
        >
          <a-form :form="formChange" class="container" @submit="updateUser">
            <div class="row">
              <div class="col-lg-6">
                <a-form-item :label="$t('utilisateur.role')">
                  <a-select
                    show-search
                    :autoFocus="true"
                    option-filter-prop="children"
                    :filter-option="filterOption"
                    :placeholder="$t('utilisateur.role')"
                    v-decorator="[
                      'role',
                      {
                        initialValue: updateData.role,
                        rules: [
                          {
                            required: true,
                            message: $t('requis.role'),
                          },
                        ],
                      },
                    ]"
                  >
                    <a-select-option
                      :value="item._id"
                      v-for="item in roleList"
                      :key="item._id"
                    >
                      {{ item.role }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
              <div class="col-lg-6">
                <a-form-item :label="$t('profil.nomUser')">
                  <a-input
                    :placeholder="$t('profil.nomUser')"
                    v-decorator="[
                      'userName',
                      {
                        initialValue: updateData.userName,
                        rules: [
                          {
                            required: true,
                            message: $t('requis.username'),
                          },
                        ],
                      },
                    ]"
                  />
                </a-form-item>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-12">
                <a-form-item :label="$t('utilisateur.acces')">
                  <a-select
                    show-search
                    :autoFocus="true"
                    :allowClear="true"
                    mode="multiple"
                    option-filter-prop="children"
                    :filter-option="filterOption"
                    style="width: 100%"
                    :placeholder="$t('utilisateur.acces')"
                    v-decorator="[
                      'buildings',
                      {
                        initialValue: updateData.buildings,
                        rules: [
                          {
                            required: true,
                            message: $t('requis.role'),
                          },
                        ],
                      },
                    ]"
                  >
                    <a-select-option
                      :value="item._id"
                      v-for="item in buildingList"
                      :key="item._id"
                    >
                      {{ item.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
            </div>
            <div class="form-actions mt-0">
              <a-button
                style="width: 150px"
                type="primary"
                htmlType="submit"
                :loading="loadingUtilisateur"
                class="mr-3"
              >
                {{ $t("action.modifier") }}
              </a-button>
              <a-button
                key="back"
                @click="
                  () => {
                    visibleModalPassword = false;
                  }
                "
                :disabled="loadingUtilisateur"
              >
                {{ $t("action.annuler") }}
              </a-button>
            </div>
          </a-form>
        </a-modal>
      <a-modal
        v-model="visibleQrCodeErrorModal"
        :title="$t('liste.qrCodeErrorWarning')"
        :width="600"
        :footer="null"
      >
        <a-alert
          :description="$t('liste.qrCodeErrorDescription')"
          type="warning"
          show-icon
          class="mb-3"
        />
        <div
          v-for="qrUser in qrCodeErrorData"
          :key="qrUser._id"
          style="margin-bottom: 8px"
        >
          <strong>{{ qrUser.fullName }}</strong>
        </div>
      </a-modal>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import apiClient from "@/services/axios";
import moment from "moment";
import { mapState } from "vuex";
import VueQr from "vue-qr";
import JsPDF from "jspdf";
import axios from "axios";
import { generatePassword } from "@/utilities/settings";
import { pdfHeader } from "@/utilities/pdfUtils";

const { AwesomeQR } = require("awesome-qr");
const { generateAllLoginPdf } = require("@/utilities/pdfAllLogin");

export default {
  beforeMount() {
    this.form = this.$form.createForm(this, { name: "userForm" });
  },
  components: { VueQr },
  computed: {
    ...mapState(["settings", "user"]),
    qrData() {
      if (!this.userToken) return "";
      return window.btoa(this.userToken + "#" + this.settings.base_url);
    },
    canManageUserSessions() {
      return this.user.role === "admin";
    },
    sessionColumns() {
      return [
        {
          title: this.$t("utilisateur.device"),
          dataIndex: "device",
          scopedSlots: { customRender: "sessionDevice" },
        },
        {
          title: this.$t("utilisateur.ipAddress"),
          dataIndex: "ip",
          customRender: (value) => value || "--",
        },
        {
          title: this.$t("utilisateur.sessionSource"),
          dataIndex: "source",
          customRender: (value) => value || "normal",
        },
        {
          title: this.$t("utilisateur.lastActive"),
          dataIndex: "lastActive",
          scopedSlots: { customRender: "sessionLastActive" },
        },
        {
          title: this.$t("utilisateur.expiresAt"),
          dataIndex: "expiresAt",
          scopedSlots: { customRender: "sessionExpiresAt" },
        },
        {
          title: this.$t("utilisateur.actions"),
          key: "actions",
          scopedSlots: { customRender: "sessionActions" },
        },
      ];
    },
    columns() {
      const columns = [
        {
          title: this.$t("profil.nomUser"),
          dataIndex: "userName",
          key: "userName",
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "userName",
          },
          onFilter: (value, record) =>
            record.userName
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              }, 0);
            }
          },
        },
        {
          title: this.$t("utilisateur.role"),
          dataIndex: "role.role",
          key: "role",
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "role",
          },
          onFilter: (value, record) =>
            record.role.role
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              });
            }
          },
        },
        {
          title: this.$t("utilisateur.acces"),
          dataIndex: "buildings",
          key: "buildings",
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "buildings",
          },
          onFilter: (value, record) => {
            for (let i = 0; i < record.buildings.length; i++) {
              if (
                record.buildings[i].name
                  .toString()
                  .toLowerCase()
                  .includes(value.toLowerCase())
              ) {
                return true;
              }
            }
          },
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              });
            }
          },
        },
        {
          title: this.$t("topBar.status"),
          dataIndex: "status",
          key: "status",
          scopedSlots: {
            customRender: "status",
          },
          filters: [
            { text: this.$t("utilisateur.active"), value: "active" },
            { text: this.$t("utilisateur.nonActive"), value: "inactive" },
          ],
          onFilter: (value, record) => record.status.indexOf(value) === 0,
        },
        {
          title: this.$t("utilisateur.mdp"),
          scopedSlots: { customRender: "operation0" },
          fixed: "right",
        },
        {
          title: this.$t("profil.gere"),
          dataIndex: "operation",
          scopedSlots: { customRender: "operation" },
          fixed: "right",
        },
        {
          title: this.$t("utilisateur.activerDesactiver"),
          dataIndex: "operation2",
          scopedSlots: { customRender: "operation2" },
          fixed: "right",
        },
      ];
      if (this.canManageUserSessions) {
        columns.splice(4, 0, {
          title: this.$t("utilisateur.sessions"),
          dataIndex: "sessionStatus",
          scopedSlots: { customRender: "sessionStatus" },
          fixed: "right",
        });
      }
      return columns;
    },
    schoolDetails() {
      const buildings = this.user.building || [];
      return (
        buildings.find((el) => el.dbName == this.settings.activeBuilding) || {}
      );
    },
    userPackLimit() {
      return (this.schoolDetails.pack && this.schoolDetails.pack.userLimit) || 0;
    },
    textColor() {
      const limit = this.userPackLimit;
      if (!limit) {
        return { color: "green" };
      }
      if (this.userNumber > limit) {
        return { color: "red" };
      }
      if (this.userNumber > limit / 2) {
        return { color: "#c9c734" };
      }
      return { color: "green" };
    },
    getBuilding() {
      const images = this.settings.image || [];
      return (
        images.find((img) => img.db == this.settings.activeBuilding) || {}
      );
    },
  },
  created() {
    this.tableLoading = true;
    this.spinLoader = true;
    apiClient
      .get(
        "/autorisation/buildingUsers/limit/" + this.settings.activeBuildingId
      )
      .then((res) => {
        console.log(res.data);
        this.userNumber = res.data.currentUsers;
      })
      .catch((e) => this.$message.error(this.$t("error.aucRole")))
      .finally(() => {
        this.spinLoader = false;
      });
    apiClient
      .post("/roles/filter", {
        query: {
          status: "active",
          $or: [
            {
              building: this.settings.activeBuildingId,
            },
            {
              role: "admin",
            },
          ],
        },
      })
      .then((res) => (this.roleList = res.data))
      .catch((e) => this.$message.error(this.$t("error.aucRole")));
    let buidlingList = [];
    (this.user.building || []).forEach((currentItem) => {
      buidlingList.push(currentItem._id);
    });
    apiClient
      .post("/buildings/filter", {
        query: {
          _id: buidlingList,
          active: true,
        },
      })
      .then((res) => (this.buildingList = res.data))
      .catch((e) => {
        console.error(e);
        this.$message.error(this.$t("error.aucEtablissement"));
      });
    apiClient
      .get("/autorisation/buildingUsers")
      .then((res) => {
        this.allUsers = res.data;
        this.applyUserTableFilters();

        this.tableLoading = false;
      })
      .catch((e) => {
        console.log(e);
        this.$message.error(this.$t("error.aucUser"));
        this.tableLoading = false;
      });
  },
  data() {
    // this.cacheData = data.map((item) => ({ ...item }));
    return {
      userNumber: 0,
      spinLoader: false,
      qrDataImage: "",
      userToken: "",
      fetchToken: false,
      loadingLogins: false,
      visibleQrCodeErrorModal: false,
      qrCodeErrorData: [],
      checkedActiveUser: true,
      filterActiveSessionsOnly: false,
      allUsers: [],
      sessionModalVisible: false,
      selectedSessionUser: null,
      selectedUserSessions: [],
      sessionsLoading: false,
      sessionLoadingUserId: null,
      revokingSessions: false,
      revokingFamilyId: null,
      formChange: this.$form.createForm(this),
      tableLoading: false,
      roleList: [],
      buildingList: [],
      editingKey: "",
      data: [],
      searchText: "",
      searchInput: null,
      searchedColumn: "",
      targetKeys: [],
      userLogin: null,
      loadingUtilisateur: false,
      form: this.$form.createForm(this),
      visibleModal: false,
      updateData: { buildings: [], role: "", userName: "", _id: "" },
      visibleModalPassword: false,
      realVisibleModalPassword: false,
      formPassword: this.$form.createForm(this),
    };
  },
  methods: {
    updateUserSessionCount(userId, activeSessionCount) {
      const count = Math.max(0, Number(activeSessionCount) || 0);
      const id = String(userId);
      const patch = {
        activeSessionCount: count,
        hasActiveSessions: count > 0,
      };
      this.allUsers = this.allUsers.map((user) =>
        String(user._id) === id ? { ...user, ...patch } : user
      );
      this.data = this.data.map((user) =>
        String(user._id) === id ? { ...user, ...patch } : user
      );
      if (
        this.selectedSessionUser &&
        String(this.selectedSessionUser._id) === id
      ) {
        this.selectedSessionUser = {
          ...this.selectedSessionUser,
          ...patch,
        };
      }
    },
    async showUserSessions(record) {
      if (!this.canManageUserSessions || !record) return;

      this.selectedSessionUser = record;
      this.selectedUserSessions = [];
      this.sessionModalVisible = true;
      this.sessionsLoading = true;
      this.sessionLoadingUserId = record._id;
      try {
        const { data } = await apiClient.get(
          `/admin-auth/users/${encodeURIComponent(record._id)}/sessions`
        );
        this.selectedUserSessions = data.sessions || [];
        this.updateUserSessionCount(
          record._id,
          this.selectedUserSessions.length
        );
      } catch (error) {
        this.sessionModalVisible = false;
        this.$message.error(
          error.response?.data?.message ||
            this.$t("utilisateur.loadSessionsError")
        );
      } finally {
        this.sessionsLoading = false;
        this.sessionLoadingUserId = null;
      }
    },
    async revokeSelectedUserSession(session) {
      if (
        !this.selectedSessionUser ||
        this.selectedSessionUser._id === this.user._id ||
        !session ||
        !session.familyId
      ) {
        return;
      }

      this.revokingFamilyId = session.familyId;
      try {
        const userId = this.selectedSessionUser._id;
        await apiClient.post(
          `/admin-auth/users/${encodeURIComponent(userId)}/sessions/revoke`,
          { familyIds: [session.familyId] }
        );
        this.selectedUserSessions = this.selectedUserSessions.filter(
          (item) => item.familyId !== session.familyId
        );
        this.updateUserSessionCount(
          userId,
          this.selectedUserSessions.length
        );
        if (this.filterActiveSessionsOnly) {
          this.applyUserTableFilters();
        }
        this.$message.success(this.$t("utilisateur.revokeSessionSuccess"));
      } catch (error) {
        this.$message.error(
          error.response?.data?.message ||
            this.$t("utilisateur.revokeSessionsError")
        );
      } finally {
        this.revokingFamilyId = null;
      }
    },
    async revokeSelectedUserSessions() {
      if (
        !this.selectedSessionUser ||
        this.selectedSessionUser._id === this.user._id
      ) {
        return;
      }

      this.revokingSessions = true;
      try {
        const userId = this.selectedSessionUser._id;
        await apiClient.post(
          `/admin-auth/users/${encodeURIComponent(
            userId
          )}/sessions/revoke-all`
        );
        this.selectedUserSessions = [];
        this.updateUserSessionCount(userId, 0);
        if (this.filterActiveSessionsOnly) {
          this.applyUserTableFilters();
        }
        this.$message.success(this.$t("utilisateur.revokeSessionsSuccess"));
      } catch (error) {
        this.$message.error(
          error.response?.data?.message ||
            this.$t("utilisateur.revokeSessionsError")
        );
      } finally {
        this.revokingSessions = false;
      }
    },
    closeSessionModal() {
      this.sessionModalVisible = false;
      this.selectedSessionUser = null;
      this.selectedUserSessions = [];
      this.revokingFamilyId = null;
    },
    formatSessionDate(value) {
      return value ? moment(value).format("YYYY-MM-DD HH:mm") : "--";
    },
    validateNoWhitespace(rule, value, callback) {
      if (/\s/.test(value)) {
        callback(new Error("Username cannot contain whitespace"));
      } else {
        callback();
      }
    },
    getQrImage(dataUrl) {
      this.qrDataImage = dataUrl;
    },
    async getUserToken(userId) {
      this.fetchToken = true;
      this.userToken = "";
      if (userId) {
        try {
          const { data } = await apiClient.get(
            "/autorisation/qrcodeinfo/" + userId
          );
          this.userToken = data.token;
        } catch (error) {
          this.userToken = "";
        }
      }
      this.fetchToken = false;
    },
    async updatePasswordModal(record) {
      this.updateData = {
        buildings: [],
        role: record.role._id,
        userName: record.userName,
        _id: record._id,
      };

      await this.getUserToken(record._id);
      this.realVisibleModalPassword = true;
    },

    updateModal(record) {
      this.updateData = {
        buildings: record.buildings.map((e) => e._id),
        role: this.roleList.some(function (el) {
          return el._id === record.role._id;
        })
          ? record.role._id
          : undefined,
        userName: record.userName,
        _id: record._id,
      };
      this.visibleModalPassword = true;
    },
    updatePassword(e) {
      e.preventDefault();
      this.formPassword.validateFieldsAndScroll((err, values) => {
        this.loadingUtilisateur = true;

        if (values.password != values.confirmPassword) {
          this.$message.error(this.error("error.verifier"));
          this.loadingUtilisateur = false;
        } else {
          apiClient
            .patch("/users", {
              userName: this.updateData.userName,
              updates: {
                password: values.password,
              },
            })
            .then(() => {
              this.$message.success(this.$t("success.utilisateurMSA"));
              this.$socket.client.emit("updateuser", {
                userId: this.updateData._id,
                userName: this.updateData.userName,
                userType: "admin",
              });
              this.generatePdf(this.updateData, values.password);
              this.$emit("reload");
            })
            .catch((e) => {
              console.error(e);
              this.$message.error(this.$t("error.modif"));
            })
            .finally(() => (this.loadingUtilisateur = false));
        }
      });
    },
    updateUser(e) {
      this.loadingUtilisateur = true;
      e.preventDefault();
      this.formChange.validateFieldsAndScroll((err, values) => {
        if (values.role) {
          apiClient
            .patch("/users", {
              userName: this.updateData.userName,
              updates: {
                userName: values.userName,
                role: values.role,
                buildings: values.buildings,
              },
            })
            .then(() => {
              this.$message.success(this.$t("success.utilisateurMSA"));
              this.$socket.client.emit("updateuser", {
                userId: this.updateData._id,
                userName: this.updateData.userName,
                userType: "admin",
              });

              this.$emit("reload");
            })
            .catch(() => {
              this.$message.error(this.$t("error.modif"));
            })
            .finally(() => (this.loadingUtilisateur = false));
        } else {
          this.$message.error(this.$t("requis.role"));
        }
      });
    },
    moment(any) {
      return moment(any);
    },
    onChange(checked) {
      this.checkedActiveUser = checked;
      this.applyUserTableFilters();
    },
    onActiveSessionsFilterChange(checked) {
      this.filterActiveSessionsOnly = checked;
      this.applyUserTableFilters();
    },
    applyUserTableFilters() {
      const status = this.checkedActiveUser ? "active" : "inactive";
      this.data = this.allUsers.filter((user) => {
        if (user.status !== status) return false;
        if (!this.filterActiveSessionsOnly) return true;
        return user.hasActiveSessions === true;
      });
      this.tableLoading = false;
    },
    showModal() {
      this.visibleModal = true;
    },
    addUtilisateur(e) {
      this.loadingUtilisateur = true;
      e.preventDefault();
      this.form.validateFieldsAndScroll((err, values) => {
        if (values.password != values.confirmPassword) {
          this.$message.error(this.$t("error.verifier"));
          this.loadingUtilisateur = false;
        } else {
          apiClient
            .put("/users", {
              userName: values.userName,
              password: values.password,
              role: values.role,
              buildings: values.buildings,
              dbName: this.settings.activeBuilding,
              buildingId: this.settings.activeBuildingId,
              status: "active",
            })
            .then(async (res) => {
              await this.getUserToken(res.data.userId);
              this.generatePdf(
                { userName: values.userName },
                values.password,
                true
              );
              this.$emit("reload");
              this.$message.success(this.$t("success.utilisateurAjout"));
              this.handleCancel();
            })
            .catch((e) => {
              if (e.response && e.response.status === 403) {
                this.$message.error(this.$t("error.maxUsersReached"));
              } else {
                console.log(e);
                this.$message.warning(this.$t("error.utilisateurNonAjoutDiff"));
              }
            })
            .finally(() => (this.loadingUtilisateur = false));
        }
      });
    },
    handleCancel(e) {
      this.visibleModal = false;
      this.visibleModalPassword = false;
    },
    handleChange(value, key, column) {
      const newData = [...this.data];
      const target = newData.filter((item) => key === item.key)[0];
      if (target) {
        target[column] = value;
        this.data = newData;
      }
    },
    edit(key) {
      const newData = [...this.data];
      const target = newData.filter((item) => key === item.key)[0];
      this.editingKey = key;
      if (target) {
        target.editable = true;
        this.data = newData;
      }
    },
    save(key) {
      const newData = [...this.data];
      const newCacheData = [...this.cacheData];
      const target = newData.filter((item) => key === item.key)[0];
      const targetCache = newCacheData.filter((item) => key === item.key)[0];
      if (target && targetCache) {
        delete target.editable;
        this.data = newData;
        Object.assign(targetCache, target);
        this.cacheData = newCacheData;
      }
      this.editingKey = "";
    },
    cancel(key) {
      const newData = [...this.data];
      const target = newData.filter((item) => key === item.key)[0];
      this.editingKey = "";
      if (target) {
        Object.assign(
          target,
          this.cacheData.filter((item) => key === item.key)[0]
        );
        delete target.editable;
        this.data = newData;
      }
    },
    onDelete(record) {
      apiClient
        .patch("/users", {
          userName: record.userName,
          updates: {
            status: "inactive",
          },
        })
        .then((r) => {
          this.data = this.data.map((elem) =>
            elem._id == record._id ? { ...elem, status: "inactive" } : elem
          );
          this.$socket.client.emit("desactivation", {
            userId: record._id,
            userName: record.userName,
            userType: "admin",
          });
          this.$message.success(this.$t("utilisateur.userDesactive"));
        })
        .catch(() => this.$$message.error(this.$t("error.modif")));
    },
    onActivate(record) {
      apiClient
        .patch("/users", {
          userName: record.userName,
          updates: {
            status: "active",
          },
        })
        .then((r) => {
          this.data = this.data.map((elem) =>
            elem._id == record._id ? { ...elem, status: "active" } : elem
          );
          this.$message.success(this.$t("utilisateur.userActive"));
        })
        .catch(() => this.$$message.error(this.$t("error.modif")));
    },
    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm();
      this.searchText = selectedKeys[0];
      this.searchedColumn = dataIndex;
    },

    handleReset(clearFilters) {
      clearFilters();
      this.searchText = "";
    },

    //Code profil selection functions
    filterOptionCodeProfil(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },

    filterOptionFilter(inputValue, option) {
      return option.description.indexOf(inputValue) > -1;
    },
    handleChange(targetKeys, direction, moveKeys) {
      this.targetKeys = targetKeys;
    },

    async generatePdfLogins() {
      try {
        this.loadingLogins = true;
        const response = await apiClient.get(
          "/autorisation/all-qrcodeinfo/all"
        );
        const adminList = response.data;
        if (!adminList.length) {
          this.$message.error(this.$t("error.aucUser"));
          return;
        }

        const result = await generateAllLoginPdf({
          parentsLogins: adminList,
          settings: this.settings,
          userType: "admin",
          t: this.$t.bind(this),
          locale: this.$i18n.locale,
        });

        if (result.qrCodeErrors && result.qrCodeErrors.length > 0) {
          this.qrCodeErrorData = result.qrCodeErrors.map((user) => ({
            fullName: `${user.firstName || ""} ${user.lastName || ""} (${
              user.userName || user.email || ""
            })`.trim(),
            _id: user._id,
          }));
          this.visibleQrCodeErrorModal = true;
        }
      } catch (error) {
        console.error(error);
        const message =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          this.$t("error.aucUser");
        this.$message.error(message);
      } finally {
        this.loadingLogins = false;
      }
    },

    async generatePdf(record, password, generateQr = false) {
      this.$gtag.event("Imp information EdManager login", {
        event_category: "Impression PDF",
        event_label: "Administration - Utilisateur section",
        value: 1,
      });

      let schoolLogo = null;
      const arrayOfImages = this.settings.image;
      for (var i = 0; i < arrayOfImages.length; i++) {
        if (this.settings.activeBuilding === arrayOfImages[i].db) {
          schoolLogo = arrayOfImages[i].logo;
          break;
        }
      }

      const titre = "EdManager : " + this.$t("pdfLogin.loginInfo");
      const doc = pdfHeader(this.settings, {
        title: titre,
        locale: this.$i18n.locale
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const startY = 50;
      const lineSpacing = 12;
      const margin = 20; // Margin from edge
      const isArabic = this.$i18n.locale === "ar-AR";

      doc.setFontSize(12);
      doc.setFont("Amiri", "normal");

      // Username
      const usernameLabel = this.$t("pdfLogin.username");
      const usernameText = isArabic
        ? `${record.userName} : ${usernameLabel}`
        : `${usernameLabel} : ${record.userName}`;
      const usernameX = isArabic
        ? pageWidth - doc.getTextWidth(usernameText) - margin
        : margin;
      doc.text(usernameX, startY, usernameText);

      // Password
      const passwordLabel = this.$t("pdfLogin.password");
      const passwordText = isArabic
        ? `${password} : ${passwordLabel}`
        : `${passwordLabel} : ${password}`;
      const passwordX = isArabic
        ? pageWidth - doc.getTextWidth(passwordText) - margin
        : margin;
      doc.text(passwordX, startY + lineSpacing, passwordText);

      // QR code instruction
      const qrInstructionText = this.$t("pdfLogin.qrCodeInstruction");
      const qrInstructionX = isArabic
        ? pageWidth - doc.getTextWidth(qrInstructionText) - margin
        : margin;
      doc.text(
        qrInstructionX,
        startY + lineSpacing * 2,
        qrInstructionText
      );

      const qrSize = 55;
      const qrY = startY + lineSpacing * 3 + 5;
      const qrX = (pageWidth - qrSize) / 2; // Center horizontally

      if (generateQr) {
        let logo = "data:image/jpeg;base64, ";

        await axios
          .get(schoolLogo || "favicon.png", {
            responseType: "arraybuffer",
          })
          .then((response) => {
            logo += Buffer.from(response.data, "binary").toString("base64");
          });

        await new AwesomeQR({
          text: this.qrData,
          logoImage: logo,
          size: 200,
          correctLevel: 1,
          logoScale: 0.2,
          logoMargin: 0,
          logoCornerRadius: 8,
          whiteMargin: true,
          dotScale: 1,
          autoColor: true,
          binarize: false,
          binarizeThreshold: 128,
          bindElement: true,
          backgroundColor: "#FFFFFF",
        })
          .draw()
          .then((dataURL) => {
            doc.addImage(dataURL, "JPEG", qrX, qrY, qrSize, qrSize);
          });
      } else {
        doc.addImage(this.qrDataImage, "JPEG", qrX, qrY, qrSize, qrSize);
      }

      //end page numbering
      doc.save(record.userName + ".pdf");
    },

    validatePass2(rule, value, callback) {
      if (value === "") {
        callback(new Error(this.$t("utilisateur.passwordAgain")));
      } else if (value !== this.form.getFieldValue("password")) {
        callback(new Error(this.$t("utilisateur.passwordMatch")));
      } else {
        callback();
      }
    },
    validatePassCh2(rule, value, callback) {
      if (value === "") {
        callback(new Error(this.$t("utilisateur.passwordAgain")));
      } else if (value !== this.formPassword.getFieldValue("password")) {
        callback(new Error(this.$t("utilisateur.passwordMatch")));
      } else {
        callback();
      }
    },
    generatePassword,
    validateStrongPassword(rule, value, callback) {
      if (!value) {
        callback(new Error(this.$t("requis.mdp")));
      } else if (value == this.form.getFieldValue("username")) {
        callback(new Error(this.$t("profil.passwordMustNotMatchUsername")));
      } else if (value.length < 8) {
        callback(new Error(this.$t("profil.passwordMinLength")));
      } else if (!/[A-Z]/.test(value)) {
        callback(new Error(this.$t("profil.passwordMustIncludeUppercase")));
      } else if (!/[a-z]/.test(value)) {
        callback(new Error(this.$t("profil.passwordMustIncludeLowercase")));
      } else if (!/[0-9]/.test(value)) {
        callback(new Error(this.$t("profil.passwordMustIncludeDigit")));
      } else {
        callback();
      }
    },
  },
};
</script>
<style scoped>
.highlight {
  background-color: rgb(255, 192, 105);
  padding: 0px;
}
.editable-row-operations a {
  margin-right: 8px;
}
.switch {
  float: right;
  margin-right: 15px;
}
.bottom {
  margin-top: 7px;
}
</style>
